import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { mockServiceOrders, mockTechnicians } from "../mock";
import { MapPin } from "lucide-react";

const ServiceMap: React.FC = () => {
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "border-yellow-500 bg-yellow-100";
      case "assigned":
        return "border-blue-500 bg-blue-100";
      case "in_progress":
        return "border-purple-500 bg-purple-100";
      case "completed":
        return "border-green-500 bg-green-100";
      default:
        return "border-gray-500 bg-gray-100";
    }
  };

  const getTechStatusColor = (status: string) => {
    switch (status) {
      case "available":
        return "border-green-500 bg-green-100";
      case "busy":
        return "border-red-500 bg-red-100";
      case "offline":
        return "border-gray-500 bg-gray-100";
      default:
        return "border-gray-500 bg-gray-100";
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Service Map View</CardTitle>
          <CardDescription>
            Interactive map showing service locations and technician positions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Map Placeholder - In a real app, this would be Google Maps or Leaflet */}
          <div className="relative h-96 bg-gradient-to-br from-blue-50 to-green-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 font-medium">Interactive Map</p>
              <p className="text-sm text-gray-500">
                Google Maps or Leaflet integration would go here
              </p>
            </div>

            {/* Mock Map Markers */}
            <div className="absolute inset-0 p-4">
              {/* Service Order Markers */}
              {mockServiceOrders.map((order, index) => (
                <div
                  key={order.id}
                  className={`absolute w-4 h-4 rounded-full border-2 cursor-pointer transform -translate-x-1/2 -translate-y-1/2 ${getStatusColor(
                    order.status
                  )}`}
                  style={{
                    left: `${20 + index * 15}%`,
                    top: `${30 + index * 10}%`,
                  }}
                  onClick={() =>
                    setSelectedOrder(
                      selectedOrder === order.id ? null : order.id
                    )
                  }
                />
              ))}

              {/* Technician Markers */}
              {mockTechnicians.map((tech, index) => (
                <div
                  key={tech.id}
                  className={`absolute w-6 h-6 rounded-full border-2 cursor-pointer transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center text-xs font-bold ${getTechStatusColor(
                    tech.status
                  )}`}
                  style={{
                    left: `${60 + index * 10}%`,
                    top: `${20 + index * 15}%`,
                  }}
                  title={tech.name}>
                  T
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service Orders */}
        <Card>
          <CardHeader>
            <CardTitle>Service Locations</CardTitle>
            <CardDescription>Active service orders by location</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {mockServiceOrders.map((order) => (
                <div
                  key={order.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedOrder === order.id
                      ? "border-blue-500 bg-blue-50"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() =>
                    setSelectedOrder(
                      selectedOrder === order.id ? null : order.id
                    )
                  }>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium">{order.title}</h4>
                      <p className="text-sm text-gray-600">
                        {order.customer.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {order.customer.address}
                      </p>
                    </div>
                    <Badge
                      className={getStatusColor(order.status)
                        .replace("border-", "bg-")
                        .replace("bg-", "bg-")
                        .replace("-100", "-100 text-")
                        .replace("text-", "text-")
                        .replace("-100", "-800")}>
                      {order.status.replace("_", " ").toUpperCase()}
                    </Badge>
                  </div>

                  {selectedOrder === order.id && (
                    <div className="mt-3 pt-3 border-t space-y-2">
                      <p className="text-sm">
                        <strong>Type:</strong> {order.type.replace("_", " ")}
                      </p>
                      <p className="text-sm">
                        <strong>Priority:</strong> {order.priority}
                      </p>
                      <p className="text-sm">
                        <strong>Description:</strong> {order.description}
                      </p>
                      {order.assignedTechnician && (
                        <p className="text-sm">
                          <strong>Technician:</strong>{" "}
                          {order.assignedTechnician.name}
                        </p>
                      )}
                      <div className="text-xs text-gray-500">
                        <p>
                          Lat: {order.customer.location.lat}, Lng:{" "}
                          {order.customer.location.lng}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Technicians */}
        <Card>
          <CardHeader>
            <CardTitle>Technician Locations</CardTitle>
            <CardDescription>
              Current position and status of field technicians
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {mockTechnicians.map((tech) => (
                <div key={tech.id} className="p-3 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium">{tech.name}</h4>
                      <p className="text-sm text-gray-600">{tech.phone}</p>
                      <p className="text-xs text-gray-500">
                        Skills: {tech.skillSet.join(", ")}
                      </p>
                      {tech.currentLocation && (
                        <p className="text-xs text-gray-500 mt-1">
                          Location: {tech.currentLocation.lat.toFixed(4)},{" "}
                          {tech.currentLocation.lng.toFixed(4)}
                        </p>
                      )}
                    </div>
                    <Badge
                      className={getTechStatusColor(tech.status)
                        .replace("border-", "bg-")
                        .replace("bg-", "bg-")
                        .replace("-100", "-100 text-")
                        .replace("text-", "text-")
                        .replace("-100", "-800")}>
                      {tech.status.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map Legend */}
      <Card>
        <CardHeader>
          <CardTitle>Map Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-4 h-4 bg-yellow-100 border-2 border-yellow-500 rounded-full mx-auto mb-1"></div>
              <p className="text-xs">Pending Orders</p>
            </div>
            <div className="text-center">
              <div className="w-4 h-4 bg-blue-100 border-2 border-blue-500 rounded-full mx-auto mb-1"></div>
              <p className="text-xs">Assigned Orders</p>
            </div>
            <div className="text-center">
              <div className="w-4 h-4 bg-purple-100 border-2 border-purple-500 rounded-full mx-auto mb-1"></div>
              <p className="text-xs">In Progress</p>
            </div>
            <div className="text-center">
              <div className="w-4 h-4 bg-green-100 border-2 border-green-500 rounded-full mx-auto mb-1"></div>
              <p className="text-xs">Completed</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="w-6 h-6 bg-green-100 border-2 border-green-500 rounded-full mx-auto mb-1 flex items-center justify-center text-xs font-bold">
                  T
                </div>
                <p className="text-xs">Available Technician</p>
              </div>
              <div className="text-center">
                <div className="w-6 h-6 bg-red-100 border-2 border-red-500 rounded-full mx-auto mb-1 flex items-center justify-center text-xs font-bold">
                  T
                </div>
                <p className="text-xs">Busy Technician</p>
              </div>
              <div className="text-center">
                <div className="w-6 h-6 bg-gray-100 border-2 border-gray-500 rounded-full mx-auto mb-1 flex items-center justify-center text-xs font-bold">
                  T
                </div>
                <p className="text-xs">Offline Technician</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceMap;
